import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Alert,
  Button,
  IconButton,
  Card,
  CardContent,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import apiClient from '../../services/api';

const EducationForm = ({ initialData = [], onSave, onCancel, loading = false }) => {
  const [educationList, setEducationList] = React.useState(
    initialData.length > 0 ? initialData : [{}]
  );
  const [errors, setErrors] = React.useState({});
  const [saveError, setSaveError] = React.useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const [isDataModified, setIsDataModified] = React.useState(false);

  // 监听initialData变化，更新表单数据
  React.useEffect(() => {
    // 只有在数据没有被修改的情况下才更新表单数据
    if (!isDataModified) {
      setEducationList(initialData.length > 0 ? initialData : [{}]);
    }
  }, [initialData, isDataModified]);

  // 监听loading状态，当外部触发保存时自动执行保存逻辑
  React.useEffect(() => {
    if (loading) {
      // 使用 setTimeout 确保在下一个事件循环中执行，避免竞态条件
      const timeoutId = setTimeout(() => {
        handleSave();
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loading]);

  // 处理表单字段变化
  const handleFormChange = (index, field, value) => {
    setEducationList(prev => {
      const newList = [...prev];
      newList[index] = {
        ...newList[index],
        [field]: value
      };
      return newList;
    });
    
    // 标记数据已被修改
    setIsDataModified(true);
    
    // 清除对应字段的错误
    const errorKey = `education.${index}.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // 添加教育经历
  const handleAddEducation = () => {
    setEducationList(prev => [...prev, {}]);
    setIsDataModified(true);
  };

  // 删除教育经历
  const handleRemoveEducation = (index) => {
    if (educationList.length > 1) {
      setEducationList(prev => prev.filter((_, i) => i !== index));
      setIsDataModified(true);
      
      // 清除相关错误
      setErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith(`education.${index}.`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  // 验证表单
  const validateForm = () => {
    const newErrors = {};
    
    educationList.forEach((education, index) => {
      if (!education.currentEducationLevel) {
        newErrors[`education.${index}.currentEducationLevel`] = '请选择教育程度';
      }
      if (!education.schoolName) {
        newErrors[`education.${index}.schoolName`] = '请输入学校名称';
      }
      
      // 只有大专、本科、硕士需要填写专业
      const requiresMajor = ['Diploma', 'Bachelor', 'Master'].includes(education.currentEducationLevel);
      if (requiresMajor && !education.major) {
        newErrors[`education.${index}.major`] = '请输入专业';
      }
      
      if (!education.enrolledDate) {
        newErrors[`education.${index}.enrolledDate`] = '请输入入学时间';
      }
      if (!education.graduationDate) {
        newErrors[`education.${index}.graduationDate`] = '请输入毕业时间';
      }
      
      // 学习模式是必填项
      if (!education.studyMode) {
        newErrors[`education.${index}.studyMode`] = '请选择学习模式';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 处理保存
  const handleSave = async () => {
    setSaveError('');
    
    if (!validateForm()) {
      // 验证失败时，显示错误提示
      setShowErrorSnackbar(true);
      // 通知父组件保存失败
      onSave && onSave(null);
      return;
    }

    try {
      const response = await apiClient.post('/api/application/update-education', {
        education: educationList
      });
      
      if (response && response.success) {
        // 保存成功后，重置修改标记
        setIsDataModified(false);
        onSave && onSave(educationList);
      } else {
        setSaveError(response?.message || '保存失败');
        setShowErrorSnackbar(true);
        onSave && onSave(null);
      }
    } catch (error) {
      console.error('保存教育信息失败:', error);
      setSaveError(error?.response?.data?.message || error.message || '保存失败');
      setShowErrorSnackbar(true);
      onSave && onSave(null);
    }
  };

  // 关闭错误提示
  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  return (
    <Box>
      {/* 移除固定位置的错误提示 */}
      
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
        教育背景信息
      </Typography>

      {educationList.map((education, index) => (
        <Card key={index} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                教育经历 {index + 1}
              </Typography>
              {educationList.length > 1 && (
                <IconButton
                  color="error"
                  onClick={() => handleRemoveEducation(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors[`education.${index}.currentEducationLevel`]}>
                  <InputLabel>教育程度 *</InputLabel>
                  <Select
                    value={education.currentEducationLevel || ''}
                    onChange={(e) => handleFormChange(index, 'currentEducationLevel', e.target.value)}
                    label="教育程度 *"
                  >
                    <MenuItem value="High School">普通高中</MenuItem>
                    <MenuItem value="Technical secondary school">职高（中专）</MenuItem>
                    <MenuItem value="International High School">国际高中</MenuItem>
                    <MenuItem value="Diploma">大专</MenuItem>
                    <MenuItem value="Bachelor">本科</MenuItem>
                    <MenuItem value="Master">硕士</MenuItem>
                  </Select>
                  {errors[`education.${index}.currentEducationLevel`] && (
                    <FormHelperText>{errors[`education.${index}.currentEducationLevel`]}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="学校名称 *"
                  value={education.schoolName || ''}
                  onChange={(e) => handleFormChange(index, 'schoolName', e.target.value)}
                  error={!!errors[`education.${index}.schoolName`]}
                  helperText={errors[`education.${index}.schoolName`]}
                />
              </Grid>
              
                             <Grid item xs={12} sm={6}>
                 <TextField
                   fullWidth
                   label={`专业${['Diploma', 'Bachelor', 'Master'].includes(education.currentEducationLevel) ? ' *' : ''}`}
                   value={education.major || ''}
                   onChange={(e) => handleFormChange(index, 'major', e.target.value)}
                   error={!!errors[`education.${index}.major`]}
                   helperText={errors[`education.${index}.major`]}
                   placeholder={['Diploma', 'Bachelor', 'Master'].includes(education.currentEducationLevel) ? '请输入专业' : '可选'}
                 />
               </Grid>
              
                             <Grid item xs={12} sm={6}>
                 <TextField
                   fullWidth
                   label="绩点"
                   value={education.gpa || ''}
                   onChange={(e) => handleFormChange(index, 'gpa', e.target.value)}
                   placeholder="例如：3.5（可选）"
                 />
               </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="入学时间 *"
                  type="date"
                  value={education.enrolledDate || ''}
                  onChange={(e) => handleFormChange(index, 'enrolledDate', e.target.value)}
                  error={!!errors[`education.${index}.enrolledDate`]}
                  helperText={errors[`education.${index}.enrolledDate`]}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="毕业时间 *"
                  type="date"
                  value={education.graduationDate || ''}
                  onChange={(e) => handleFormChange(index, 'graduationDate', e.target.value)}
                  error={!!errors[`education.${index}.graduationDate`]}
                  helperText={errors[`education.${index}.graduationDate`]}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
                             <Grid item xs={12} sm={6}>
                 <FormControl fullWidth error={!!errors[`education.${index}.studyMode`]}>
                   <InputLabel>学习模式 *</InputLabel>
                   <Select
                     value={education.studyMode || ''}
                     onChange={(e) => handleFormChange(index, 'studyMode', e.target.value)}
                     label="学习模式 *"
                   >
                     <MenuItem value="Full-time">全日制</MenuItem>
                     <MenuItem value="Part-time">非全日制</MenuItem>
                   </Select>
                   {errors[`education.${index}.studyMode`] && (
                     <FormHelperText>{errors[`education.${index}.studyMode`]}</FormHelperText>
                   )}
                 </FormControl>
               </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
      
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddEducation}
        sx={{ mb: 3 }}
      >
        添加教育经历
      </Button>

      {/* 浮动错误提示 */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseErrorSnackbar} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {saveError || '表单验证失败，请检查输入信息'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EducationForm; 